import { Color3 } from '@babylonjs/core/Maths/math.color';
import { CreateCylinder } from '@babylonjs/core/Meshes/Builders/cylinderBuilder';
import type { Mesh } from '@babylonjs/core/Meshes/mesh';
import { StandardMaterial } from '@babylonjs/core/Materials/standardMaterial';
import type { Scene } from '@babylonjs/core/scene';

import type { WhiteboardLayout } from '../core/WhiteboardLayout';
import type { StrokeDto } from '../generated/contracts';
import type { GrabberHub } from '../input/Grabbers';
import { transcribeScan } from '../net/api';
import type { RoomHub } from '../net/roomHub';
import type { RoomState } from '../room/RoomState';
import type { RoomView } from '../room/RoomView';
import { remoteBoardPoint } from './DrawingManager';
import {
  boundingRegion,
  characterHeight,
  fitStrokesToBoard,
  layoutTextStrokes,
  strokesInRange,
  type BoardBounds,
  type ScanRegion,
} from './ScanRegion';
import type { ToolMode } from './ToolMode';

/**
 * The scan tool: read a selected piece of handwriting and redraw it cleanly
 * (`plan/whiteboard.md`, "The scan tool"). Selected the same way `draw` is — a flick on the
 * dominant thumbstick's X axis (`AuthoringController.tickToolCycle`, `ToolMode`) — and, now, Y
 * is bound too: it resizes the scan line, this tool's own variant axis, the identical shape
 * `draw`'s Y axis already has for marker width (`AuthoringController`'s per-mode Y dispatch).
 * This reverses an earlier decision, recorded in `plan/whiteboard.md`, that Y should stay unbound
 * here because "a scan is one all-or-nothing action, not a continuum like marker width" — the
 * owner asked for exactly that continuum directly (*"up/down in the right stick in scan mode
 * should increase/decrease the scan line size"*), and a sizable line turned out to answer a real
 * defect, not just a missing nicety: see `lineLength`'s own doc below.
 *
 * **The gesture.** While `mode === 'scan'`, a vertical line tracks wherever the dominant hand
 * is aiming on the board (the identical aim ray `DrawingManager`'s own remote-draw preview
 * already uses — `remoteBoardPoint`, reused rather than reimplemented), both horizontally and
 * now vertically too — see `showLine`. Pulling the dominant trigger sets one edge of the X range,
 * and locks in the line's *current* vertical centre as the scan's Y window, at the line's
 * current X and Y; releasing the trigger sets the X range's other edge — order does not matter,
 * `strokesInRange`/`finishScan` sort the two X edges themselves. This
 * reserves the dominant trigger exactly the way draw mode's remote-draw path already does
 * (`ToolMode.isToolActive`, `ConnectionDrawer`'s and `PossessionManager`'s own guards), so
 * connecting and grabbing are both refused for the whole time a scan is in progress —
 * CLAUDE.md's "Gestures are arbitrated in one place" applies to a new tool the same way it did
 * to the two before it.
 *
 * **The Y extent is now a hard bound, not merely a visual aid.** Every stroke with at least one
 * point inside the swept X range *and* inside the line's own vertical window is caught *whole* —
 * a straddling stroke is not clipped to either axis — and the region actually replaced is the
 * bounding box of every point of every caught stroke (`ScanRegion.boundingRegion`), which can
 * therefore still exceed the window slightly, the same "caught whole, not clipped" latitude X
 * already had. What changed is that a stroke *outside* the window on the Y axis is no longer
 * caught at all, regardless of what it does on X.
 *
 * This reverses the original decision (still visible in `plan/whiteboard.md`'s own account of
 * it) to leave Y unbound *by construction* — every stroke touching the swept column, at any
 * height, was caught. That reads as "reads the whole board" the moment a real board has more
 * than one line of writing sharing a column: a headset report of exactly that (*"OCR works,
 * though it scans the full board"*) is what this bound exists to fix. `lineLength` is the
 * window's height in metres, and `AuthoringController`'s per-mode Y dispatch (see that class's
 * own doc) is what lets the owner size it live, rather than a fixed constant nobody could ever
 * see or tune from inside the headset.
 *
 * **On release** (`finishScan`): render the caught strokes to an offscreen 2D canvas
 * (`rasterizeStrokesToPng`), POST that image to the scan tool's own sibling transcribe route
 * (`ScanEndpoints.cs` — see that file's own remarks for why it is a sibling of
 * `ExportEndpoints.TranscribeExport` rather than a generalisation of it), and only past that
 * point — with a real, non-empty transcript in hand — delete the caught strokes and draw the
 * recognised text back as new single-stroke-font strokes (`StrokeFont.ts`/`ScanRegion
 * .layoutTextStrokes`) in the first caught stroke's own colour and width, positioned to the
 * region that was cleared but **sized to the caught ink's own typical character height**
 * (`ScanRegion.characterHeight`), not to that region's own vertical span — a scan window wide
 * enough to catch several separate lines of ordinary handwriting at once has a union span far
 * taller than any one of them, which used to come back as huge text for small handwriting; see
 * `characterHeight`'s own doc. The laid-out result is then shrunk (never enlarged) to fit
 * inside the board's own edges if it would otherwise overflow onto the wall
 * (`ScanRegion.fitStrokesToBoard`) — a guard kept independent of the sizing fix above, since a
 * transcript that is simply long enough can still overflow at a perfectly correct size.
 * **A failed call, a network error, or a genuinely empty transcription (no legible handwriting)
 * all take the identical early return: nothing caught is ever deleted, nothing is ever drawn.**
 * That property — not the font, not the region math — is the one that actually protects
 * someone's handwriting; see this class's own `finishScan`.
 *
 * No undo, no auto-snapshot, deliberately — the owner: *"We add undo/snapshots if it's
 * unreliable."* The one guard that already exists is the one above: refusing to touch anything
 * until a real transcript has come back.
 */

// spec: SPEC-098

/** Rasterises the caught strokes onto an offscreen 2D canvas and returns a PNG blob — the
 *  client-side half of the pipeline the brief asks for ("no need to go through the SVG export
 *  or rasterise anything server-side"). A parameter of `ScanTool` rather than hardwired, the
 *  same dependency-injection shape `PortalExports.ts` already uses for
 *  `rasterizeExportToPng` — so `ScanTool.test.ts` can substitute a stub that needs no real
 *  `<canvas>` 2D context, which a plain `NullEngine` test environment does not provide. */
export type ScanRasterizer = (strokes: readonly StrokeDto[], region: ScanRegion) => Promise<Blob>;

/** Reads the text back off a transcription result, or `null` for a failed/empty one — the
 *  scan tool's own dependency-injection seam for `ScanTool.test.ts`, matching `ScanRasterizer`
 *  above. The real implementation posts to `ScanEndpoints.cs`'s sibling route. */
export type ScanTranscriber = (image: Blob) => Promise<string | null>;

/** Pixels per board metre for the rasterised selection — generous enough for Gemini to read an
 *  ordinary handwriting-sized mark without producing an unreasonably large image. Not tuned
 *  against a real call. */
const PIXELS_PER_METRE = 500;

/** Blank margin kept around the caught strokes' own bounding box, in pixels — a stroke sampled
 *  exactly at the region's edge should not have half its ink cut off by the canvas edge. */
const CANVAS_PADDING_PX = 24;

/** Hard ceiling on either canvas dimension — ExportEndpoints/ScanEndpoints already cap the
 *  posted image at 8MB server-side; this keeps an unreasonably large sweep (someone dragging
 *  all the way across an oversized board) from producing a canvas that chokes the browser
 *  before it ever reaches that check. */
const MAX_CANVAS_DIMENSION_PX = 4000;

/**
 * Stroke diameter in metres, by width — matches `RoomView.ts`'s own `STROKE_RADIUS_*`
 * constants (doubled to a diameter), duplicated rather than imported because that file does
 * not export them and exact visual-weight parity with the live board is a legibility
 * nice-to-have here, not a correctness requirement the way the board's own rendering is.
 */
const STROKE_DIAMETER_METRES: Readonly<Record<string, number>> = {
  fine: 0.003,
  medium: 0.006,
  fat: 0.012,
};

/** Renders `strokes` onto a white offscreen canvas sized to `region` (plus padding) and returns
 *  a PNG blob. Only ever runs in a browser — `document.createElement('canvas')` needs a real or
 *  jsdom-mocked DOM, which is exactly why `ScanTool` takes this as an injectable dependency
 *  rather than calling it directly, the same shape `ExportRasterizer.ts` already established. */
export async function rasterizeStrokesToPng(strokes: readonly StrokeDto[], region: ScanRegion): Promise<Blob> {
  const widthPx = clampDimension((region.maxX - region.minX) * PIXELS_PER_METRE + CANVAS_PADDING_PX * 2);
  const heightPx = clampDimension((region.maxY - region.minY) * PIXELS_PER_METRE + CANVAS_PADDING_PX * 2);

  const canvas = document.createElement('canvas');
  canvas.width = widthPx;
  canvas.height = heightPx;

  const ctx = canvas.getContext('2d');
  if (!ctx) {
    throw new Error('Could not get a 2D canvas context to rasterise the scanned selection.');
  }

  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, widthPx, heightPx);
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';

  for (const stroke of strokes) {
    if (stroke.points.length < 2) {
      continue;
    }

    const diameterMetres = STROKE_DIAMETER_METRES[stroke.width ?? 'medium'] ?? STROKE_DIAMETER_METRES.medium!;
    ctx.strokeStyle = stroke.color;
    ctx.lineWidth = Math.max(1, diameterMetres * PIXELS_PER_METRE);

    ctx.beginPath();

    stroke.points.forEach((p, index) => {
      const x = CANVAS_PADDING_PX + (p.x - region.minX) * PIXELS_PER_METRE;
      // Canvas y grows downward; board y grows upward — flip against the region's own top.
      const y = CANVAS_PADDING_PX + (region.maxY - p.y) * PIXELS_PER_METRE;

      if (index === 0) {
        ctx.moveTo(x, y);
      } else {
        ctx.lineTo(x, y);
      }
    });

    ctx.stroke();
  }

  return await new Promise<Blob>((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (blob) {
        resolve(blob);
      } else {
        reject(new Error('Rasterising the scanned selection produced no image data.'));
      }
    }, 'image/png');
  });
}

function clampDimension(px: number): number {
  return Math.min(MAX_CANVAS_DIMENSION_PX, Math.max(1, Math.round(px)));
}

/** Crosses a `WhiteboardLayout`'s centre/width/height into the plain `BoardBounds` rectangle
 *  `fitStrokesToBoard` (ScanRegion.ts, kept Babylon-free) takes as data — the same room-local
 *  x/y a stroke's own points and `remoteBoardPoint` already share (`DrawingManager.ts`). */
function boardBounds(whiteboard: WhiteboardLayout): BoardBounds {
  return {
    minX: whiteboard.center.x - whiteboard.width / 2,
    maxX: whiteboard.center.x + whiteboard.width / 2,
    minY: whiteboard.center.y - whiteboard.height / 2,
    maxY: whiteboard.center.y + whiteboard.height / 2,
  };
}

/** How far in front of the board's own surface the range line sits, along the board's normal,
 *  toward the room — matches DrawingManager's own AIM_INDICATOR_OFFSET, enough to clear
 *  z-fighting with the board mesh behind it. */
const LINE_OFFSET = 0.01;

const LINE_DIAMETER = 0.006;
const LINE_COLOR = '#FFD166';

/**
 * The scan line's own length in metres — the height of the hard vertical window a scan catches
 * strokes within (see the class's own doc, "The Y extent is now a hard bound"). Three named
 * constants rather than a slider, per the owner's own instruction ("tuning should be a one-line
 * edit"):
 *
 * - `SCAN_LINE_MIN_LENGTH` — small enough to isolate a single line of ordinary handwriting
 *   without needing pixel-perfect vertical aim.
 * - `SCAN_LINE_MAX_LENGTH` — close to a full board's own drawable height (`BOARD_TOP_HEIGHT -
 *   BOARD_BASE_HEIGHT` in `WhiteboardLayout.ts` is 1.6m), so winding the line all the way up
 *   still leaves *some* vertical selectivity rather than silently degrading back to "the whole
 *   board" the fix above exists to prevent.
 * - `SCAN_LINE_DEFAULT_LENGTH` — comfortably covers a couple of lines of stacked text without
 *   the owner ever having to touch the stick for an ordinary scan.
 *
 * Illustrative numbers, not measured against a real headset pass — a starting point the same way
 * `TOUCH_ENGAGE_DISTANCE`/`TOUCH_RELEASE_DISTANCE` in `DrawingManager.ts` were.
 */
const SCAN_LINE_MIN_LENGTH = 0.1;
const SCAN_LINE_MAX_LENGTH = 1.6;
const SCAN_LINE_DEFAULT_LENGTH = 0.4;
const SCAN_LINE_LENGTH_STEP = 0.1;

interface PendingRange {
  readonly startX: number;
  /** The line's own vertical centre at the moment the trigger pressed — locked in for the whole
   *  gesture rather than re-read at release, the same reasoning `PenLedge`'s successors always
   *  give a value that must not drift mid-gesture: a horizontal sweep is exactly the motion most
   *  likely to also drift the hand vertically, and locking the window at press keeps that drift
   *  from silently shrinking or relocating what gets caught out from under the user. */
  readonly centerY: number;
}

export class ScanTool {
  private lineMesh: Mesh | null = null;
  private lineMaterial: StandardMaterial | null = null;

  /** Set once the trigger has pressed while aiming at the board; cleared on release (whether
   *  finished or discarded) or on any early-return in `tick` (mode left, grabber lost, no
   *  whiteboard). Discarding it is always side-effect-free — nothing about a caught stroke is
   *  ever touched until `finishScan` gets a real transcript back. */
  private pending: PendingRange | null = null;

  /** The most recent board-X the aim ray actually landed on — kept so a release whose own
   *  aim strayed off the board's rectangle at the exact release frame can still finalise the
   *  range from wherever it last validly was, rather than silently dropping the gesture. */
  private lastValidX: number | null = null;

  /** True for the whole async tail of `finishScan` — guards against starting a second sweep
   *  (and a second, overlapping delete-and-redraw) before the first one has finished. */
  private processing = false;

  /** The scan line's own length, in metres — the height of the hard vertical window a scan
   *  catches strokes within. Adjusted live by `AuthoringController`'s per-mode Y dispatch, the
   *  scan tool's own answer to `ToolMode.cycleDrawVariant`'s question for `draw`. See the class
   *  doc, "The Y extent is now a hard bound," and `SCAN_LINE_DEFAULT_LENGTH`'s own doc for why
   *  this starts where it does. */
  private currentLineLength = SCAN_LINE_DEFAULT_LENGTH;

  private readonly transcribe: ScanTranscriber;
  private readonly rasterize: ScanRasterizer;

  /** Surfaced to the user on refusal, failure, or success; the caller decides how. */
  onNotice: ((message: string) => void) | null = null;

  constructor(
    private readonly scene: Scene,
    private readonly grabbers: GrabberHub,
    private readonly state: RoomState,
    private readonly view: RoomView,
    private readonly hub: RoomHub,
    private readonly toolMode: ToolMode,
    private readonly whiteboard: WhiteboardLayout | null,
    private readonly context: { roomId: string },
    deps: { transcribe?: ScanTranscriber; rasterize?: ScanRasterizer } = {},
  ) {
    this.transcribe =
      deps.transcribe ?? ((image) => transcribeScan(this.context.roomId, image).then((result) => result.text ?? null));
    this.rasterize = deps.rasterize ?? rasterizeStrokesToPng;
  }

  start(): () => void {
    const observer = this.scene.onBeforeRenderObservable.add(() => this.tick());

    return () => this.scene.onBeforeRenderObservable.remove(observer);
  }

  dispose(): void {
    this.pending = null;
    this.lastValidX = null;

    // `finishScan` awaits a real network round trip (rasterize, then transcribe) seconds long
    // in production, and only ever clears `processing` itself in its own `finally` — nothing
    // else does, ordinarily. Clearing it here too doubles as the disposal signal `finishScan`
    // checks for at every resumption point below (`if (!this.processing) return;`): a
    // navigate-away mid-round-trip is otherwise indistinguishable from an ordinary in-flight
    // scan, and the async tail would keep running after teardown and land hub calls
    // (deleteStroke, commitStroke) and a view mutation (endScanPulse) on a tool nobody holds a
    // reference to any more.
    this.processing = false;

    this.lineMaterial?.dispose();
    this.lineMesh?.dispose();
    this.lineMesh = null;
    this.lineMaterial = null;

    // Own no material of its own to dispose here — the glow lives on each stroke's own
    // pre-existing material, owned and disposed by RoomView. Just stop touching it.
    this.view.setScanSelection(new Set());
  }

  /** Whether a range sweep is currently in progress — one edge set, waiting for the release
   *  that sets the other. Exposed for tests; nothing in this class reads it itself. */
  get isSelecting(): boolean {
    return this.pending !== null;
  }

  /** The scan line's current length, in metres. Exposed for `AuthoringController`'s floating
   *  label and for tests; nothing in this class reads its own getter. */
  get lineLength(): number {
    return this.currentLineLength;
  }

  /**
   * Steps the scan line's length by one `SCAN_LINE_LENGTH_STEP`, clamped to
   * `[SCAN_LINE_MIN_LENGTH, SCAN_LINE_MAX_LENGTH]` — the scan tool's own half of
   * `AuthoringController`'s per-mode Y dispatch, the same wrap-free clamped-step shape
   * `ToolMode.cycleDrawVariant` uses for `draw` (that one wraps because a fixed set of positions
   * has no natural "too far"; a length has real min/max ends, so this clamps instead of wrapping).
   */
  adjustLineLength(direction: 1 | -1): void {
    const stepped = this.currentLineLength + direction * SCAN_LINE_LENGTH_STEP;
    this.currentLineLength = Math.min(SCAN_LINE_MAX_LENGTH, Math.max(SCAN_LINE_MIN_LENGTH, stepped));
  }

  private tick(): void {
    const grabber = this.grabbers.grabbers.find((candidate) => candidate.role === 'dominant');
    const whiteboard = this.whiteboard;
    const active = this.toolMode.isScanMode;

    if (!grabber || !whiteboard || !active) {
      this.hideLine();
      this.pending = null;

      // Leaving mode/losing the grabber does not interrupt an in-flight scan (`processing`) —
      // its pulse (state 2) is driven independently by `beginScanPulse`/`tickScanGlow`, not by
      // this tick's own live-selection updates, so it must not be cleared here.
      if (!this.processing) {
        this.view.setScanSelection(new Set());
      }

      return;
    }

    const point = remoteBoardPoint(grabber, whiteboard);

    if (point) {
      this.lastValidX = point.x;
      this.showLine(point.x, point.y, whiteboard);
    } else {
      this.hideLine();
    }

    // State 1, "live selection, while sweeping" (and even before the trigger is first
    // pressed — the owner's own headset report this answers, "it scans the full board," is
    // fixed by letting the user see what the line's current position and length would catch
    // *before* committing to anything). Skipped while a previous scan is still processing —
    // `beginScanPulse`/`endScanPulse` own the glow for those strokes instead, and this tick's
    // idea of "what's under the line right now" has nothing to do with what is already caught.
    if (!this.processing) {
      this.updateSelection(point);
    }

    if (grabber.far.justPressed && this.pending === null && !this.processing) {
      // A press while not aimed at the board sets nothing — the same requirement
      // remoteBoardPoint already enforces for draw mode's remote path (aiming away from the
      // board does not draw). The user has to be aiming at the board for the gesture to begin
      // at all; holding the trigger and then swinging onto the board does not retroactively
      // start one.
      if (point) {
        this.pending = { startX: point.x, centerY: point.y };
      }

      return;
    }

    if (grabber.far.justReleased && this.pending !== null) {
      const startX = this.pending.startX;
      const centerY = this.pending.centerY;
      const endX = point ? point.x : this.lastValidX;
      this.pending = null;

      if (endX !== null) {
        void this.finishScan(startX, endX, centerY);
      } else {
        // No valid X was ever sampled (aimed off the board for the whole sweep) — nothing to
        // scan, so the live-selection glow (state 1) has nothing left to show either.
        this.view.setScanSelection(new Set());
      }
    }
  }

  /**
   * Recomputes and applies the live-selection glow (state 1) for wherever the scan line
   * currently is — the exact same `strokesInRange` call `finishScan` will make on release, run
   * here every tick so what glows is always "what would be caught right now," not a stale
   * snapshot. Before a press (`this.pending === null`), both X edges are the current aim point
   * — a zero-width column — so this also answers "what's directly under the line," the state
   * the owner's own report needs visible *before* the trigger is ever pressed at all.
   */
  private updateSelection(point: { readonly x: number; readonly y: number } | null): void {
    if (!point) {
      this.view.setScanSelection(new Set());
      return;
    }

    const halfLength = this.currentLineLength / 2;
    const xEdgeA = this.pending ? this.pending.startX : point.x;
    const centerY = this.pending ? this.pending.centerY : point.y;
    // Before a press, `xEdgeA` and `point.x` are the same value — and even after one, the very
    // first tick following the press has `pending.startX === point.x` too, since no movement
    // has happened yet. A genuinely zero-width range is not a harmless degenerate case here:
    // `remoteBoardPoint`'s own ray-plane intersection reconstructs the aimed board coordinate
    // through division, not an exact inverse of whatever produced the aim direction, so it can
    // land a sub-micrometre off a stroke's own stored point — which a literal `[x, x]` range
    // then excludes outright, even though the line is visibly right on top of it. Widened to at
    // least the line's own rendered diameter (`LINE_DIAMETER`) when it collapses this close,
    // so the live preview matches what the line actually looks like on the board rather than an
    // idealised infinitely-thin ray a real render can never be true to.
    const loX = Math.min(xEdgeA, point.x);
    const hiX = Math.max(xEdgeA, point.x);
    const previewHalfWidth = LINE_DIAMETER / 2;
    const midX = (loX + hiX) / 2;
    const [rangeLoX, rangeHiX] =
      hiX - loX < LINE_DIAMETER ? [midX - previewHalfWidth, midX + previewHalfWidth] : [loX, hiX];

    const caught = strokesInRange(this.state.strokes.values(), rangeLoX, rangeHiX, [
      centerY - halfLength,
      centerY + halfLength,
    ]);

    this.view.setScanSelection(new Set(caught.map((stroke) => stroke.id)));
  }

  /**
   * The whole capture-transcribe-replace pipeline. The property that matters most — a failed
   * or empty transcription deletes nothing — is enforced by literal control flow: nothing that
   * mutates a stroke appears anywhere before the `text` check below returns.
   */
  private async finishScan(xEdgeA: number, xEdgeB: number, centerY: number): Promise<void> {
    this.processing = true;

    // Whether this run reached the point of actually committing a replacement — read by the
    // `finally` below to decide how `endScanPulse` ends the round-trip glow (state 2). Starts
    // `false` and flips to `true` at exactly one place, right before the delete-and-redraw
    // loop, mirroring the "nothing that mutates a stroke appears before the `text` check"
    // property this method's own doc already promises — the glow's own success/failure split
    // rides the identical boundary rather than a second, separately-maintained condition.
    let succeeded = false;

    try {
      const halfLength = this.currentLineLength / 2;
      const caught = strokesInRange(this.state.strokes.values(), xEdgeA, xEdgeB, [
        centerY - halfLength,
        centerY + halfLength,
      ]);

      if (caught.length === 0) {
        this.onNotice?.('nothing there to scan');
        return;
      }

      // State 2, "pulsing during the round trip" — begins the instant a real, non-empty catch
      // is confirmed, so the wait for Gemini (a real network round trip, seconds long) reads as
      // work in progress rather than a freeze. Ended in the `finally` below on every exit path,
      // not repeated at each one — `RoomView.endScanPulse` is itself a no-op if this was never
      // called for a given run (never reached here) or already ended.
      this.view.beginScanPulse(new Set(caught.map((stroke) => stroke.id)));

      const region = boundingRegion(caught);

      // Unreachable given caught.length > 0 (boundingRegion only returns null for an empty
      // list), but keeps this function's own control flow honest without a non-null assertion.
      if (!region) {
        return;
      }

      let image: Blob;

      try {
        image = await this.rasterize(caught, region);
      } catch (error) {
        this.onNotice?.(error instanceof Error ? error.message : String(error));
        return;
      }

      // Disposed while suspended in `rasterize` — a navigate-away mid-round-trip
      // (`dispose()` clears `processing`, which this method itself otherwise only ever clears
      // in its own `finally`, below). Bail before touching the hub or the view at all; see
      // `dispose()`'s own comment for why `processing` doubles as this signal.
      if (!this.processing) {
        return;
      }

      let text: string | null;

      try {
        text = await this.transcribe(image);
      } catch (error) {
        // A network failure or a server refusal (503 unconfigured, 400 malformed) reads
        // exactly like an empty transcription from here — nothing caught is touched either
        // way, which is the one property this whole method exists to guarantee.
        this.onNotice?.(error instanceof Error ? error.message : String(error));
        return;
      }

      // Same disposal check, after the other seconds-long network round trip.
      if (!this.processing) {
        return;
      }

      if (text === null || text.trim().length === 0) {
        this.onNotice?.('no legible handwriting found there — nothing changed');
        return;
      }

      // Past this point, and only past this point, does anything about the board actually
      // change: a real, non-empty transcript is in hand. Also the point past which the
      // round-trip glow (state 2) ends in success, not a fade-out — the caught strokes are
      // about to be deleted below, and `RoomView.onStrokeRemoved`'s own delete-fade takes over
      // from here rather than this class's glow easing back to a baseline these strokes will
      // never show again.
      succeeded = true;

      for (const stroke of caught) {
        // Checked on every iteration, not just once before the loop: each `deleteStroke` is
        // its own network round trip, so disposal can land between any two of them too.
        if (!this.processing) {
          return;
        }

        try {
          await this.hub.deleteStroke(stroke.id);
          this.state.removeStroke(stroke.id);
        } catch (error) {
          this.onNotice?.(error instanceof Error ? error.message : String(error));
        }
      }

      if (!this.processing) {
        return;
      }

      // "the same colour" (the brief's own words) — the first caught stroke's, picked
      // deterministically by array order rather than averaged or otherwise invented, the same
      // reasoning that would apply to width if the caught set mixed those too.
      const color = caught[0]!.color;
      const width = caught[0]!.width ?? undefined;

      // Sized off the caught ink's own typical character height, not the (possibly much
      // taller) union span between separate lines the scan window happened to catch together —
      // see `characterHeight`'s own doc in ScanRegion.ts for the headset report this answers.
      const textHeight = characterHeight(caught);
      const laidOut = layoutTextStrokes(text, region, { textHeight });

      // Hard cap, on top of the sizing above: the replacement's own bounding box must never
      // exceed the caught ink's own bounding box (`region` — `boundingRegion(caught)`), in
      // width or height, full stop. Owner: *"Still some inconsistencies with sizing of output,
      // should be a hard cap that says that output can never be bigger than the original scan
      // area."* `characterHeight` sizing is a good *typical* answer, not a *guaranteed* one —
      // Hershey glyph widths vary per character (`StrokeFont.ts`'s own advance widths are not
      // uniform), a transcript with unusually wide characters or simply more of them than the
      // caught ink's own line was can still lay out wider than the ink it replaced even at the
      // "right" per-character height, and the 'i'/'j' dot overshoot noted in `ScanRegion.ts`
      // ("Real descenders") can push height a hair past it too — both demonstrated above by the
      // "hi" fixture in `ScanTool.test.ts`, whose replacement needs this exact cap to stay
      // inside its own two-point source stroke's bounding box. `region` and `BoardBounds` share
      // a shape (`region`'s extra `z` field is simply ignored), so this reuses
      // `fitStrokesToBoard` verbatim rather than a second shrink-to-fit implementation — same
      // uniform, never-enlarge, anchor-and-translate behaviour, applied against a tighter
      // rectangle. This is strictly stronger than the owner's own words ("the original scan
      // area", the swept X/Y window) — every caught point already sits inside that window by
      // construction (`strokesInRange`), and `region` is a subset of it (the caught ink's own
      // tight bbox, not the whole window) — so satisfying this satisfies that too.
      //
      // Deliberately no floor on how small this can shrink text to. A transcript that is long
      // relative to a small ink bbox becomes small, possibly to the point of being hard to read
      // without leaning in — accepted rather than fought, for two reasons: first, the hard cap
      // above is the owner's own explicit ask, stated with no carve-out for "unless it gets too
      // small," and a floor would silently reintroduce the oversized-output bug this exists to
      // fix, just bounded instead of unbounded; second, the ink bbox came from the user's own
      // sweep — a tightly-drawn selection is deliberately taken as a genuine size signal, and if
      // it is systematically too small the fix is a wider sweep (or a taller scan line, already
      // user-adjustable — `adjustLineLength`), not a tool that quietly ignores its own cap.
      const cappedToInk = fitStrokesToBoard(laidOut, region);

      // Independent overflow guard: shrink-to-fit inside the board's own edges regardless of
      // whether the sizing above already made this unreachable in the common case — a
      // genuinely long transcript at a *correct* size can still be wider than the board, and
      // "the sizing is right now, so this cannot overflow" is exactly the reasoning that would
      // let that ship unfixed. No-op when the laid-out text already fits. Kept even though the
      // ink cap above already guarantees the result fits inside the board in the ordinary case
      // (the ink itself is always on the board) — cheap, and a distinct guard against a
      // different failure (a selection taken right at the board's own edge, where the ink's own
      // bbox can still poke past it).
      const replacements = this.whiteboard ? fitStrokesToBoard(cappedToInk, boardBounds(this.whiteboard)) : cappedToInk;

      for (const replacement of replacements) {
        if (!this.processing) {
          return;
        }

        try {
          const committed = await this.hub.commitStroke({
            strokeId: crypto.randomUUID(),
            color,
            width,
            points: replacement.points,
          });

          this.state.upsertStroke(committed);
        } catch (error) {
          this.onNotice?.(error instanceof Error ? error.message : String(error));
        }
      }

      if (this.processing) {
        this.onNotice?.('rewritten');
      }
    } finally {
      // `dispose()` clears `processing` too, so seeing it already `false` here can only mean
      // it ran while this method was suspended (this method's own code never clears it before
      // this point) — in which case the round-trip glow belongs to a tool nobody holds a
      // reference to any more, and touching `view` for it is exactly the kind of after-dispose
      // call this whole guard exists to stop. No early `return` inside this `finally` — that
      // would risk swallowing an exception the `try` above was propagating — so the guard is
      // an `if` around the one thing left to skip instead.
      const disposedMidFlight = !this.processing;
      this.processing = false;

      if (!disposedMidFlight) {
        // State 3, "fade out on nothing found," is exactly `endScanPulse(fade: true)` — every
        // early return above (nothing caught aside, which never started a pulse to end) leaves
        // `succeeded` `false`: a rasterise failure, a transcribe failure, and a genuinely empty
        // transcription all take this same path, matching this method's own "a failed or empty
        // transcription deletes nothing" property one level up — the glow says so too, rather
        // than snapping off silently.
        this.view.endScanPulse(!succeeded);
      }
    }
  }

  /**
   * Built once at a fixed unit height (1m) rather than at `currentLineLength` directly, because
   * the length now changes live while the tool is in use — `showLine` stretches it every tick
   * via `mesh.scaling.y` instead of rebuilding the cylinder's geometry, the same "scale, don't
   * rebuild" shape a per-frame size change calls for anywhere in this project.
   */
  private ensureLine(): { mesh: Mesh; material: StandardMaterial } {
    if (this.lineMesh && this.lineMaterial) {
      return { mesh: this.lineMesh, material: this.lineMaterial };
    }

    const mesh = CreateCylinder(
      'scan-range-line',
      { height: 1, diameter: LINE_DIAMETER, tessellation: 6 },
      this.scene,
    );
    mesh.isPickable = false;
    mesh.setEnabled(false);

    const material = new StandardMaterial('scan-range-line-material', this.scene);
    const color = Color3.FromHexString(LINE_COLOR);
    material.diffuseColor = color;
    material.emissiveColor = color.scale(0.6);
    material.specularColor = Color3.Black();
    mesh.material = material;

    this.lineMesh = mesh;
    this.lineMaterial = material;

    return { mesh, material };
  }

  /**
   * Positions the line at the current aim, both axes now — `y` used to be ignored in favour of
   * the board's own vertical centre, back when the line always spanned the full board height
   * (see the class doc's account of why that changed). `mesh.scaling.y` is set to
   * `currentLineLength` every tick rather than once, so a mid-hold length change (the whole
   * point of binding it to the stick) is visible immediately, not just on the next press.
   */
  private showLine(x: number, y: number, whiteboard: WhiteboardLayout): void {
    const { mesh } = this.ensureLine();
    const base = whiteboard.center.add(whiteboard.normal.scale(LINE_OFFSET));

    mesh.position.set(x, y, base.z);
    mesh.scaling.y = this.currentLineLength;
    mesh.setEnabled(true);
  }

  private hideLine(): void {
    this.lineMesh?.setEnabled(false);
  }
}
